import React, { Fragment, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { formatDate } from '../../../utils/dateFunctions';
import Button from '../../formElements/Button';

import pdf from '../../../commonStyles/pdf.module.css';
import classes from './actionPlan.module.css';

const GenerateActionPlanPDF = ({
    participant,
    barriers,
    actions,
    templateContainerRef,
    onClosePdfPreview
}) => {
    const documentId = actions[actions.length - 1]?.id?.slice(-9) || '-';

    // LOCAL STATE
    const [allowPDFPrint, setAllowPDFPrint] = useState(true);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);

    // USE EFFECTS
    useEffect(() => {
        if (templateContainerRef.current && allowPDFPrint) {
            setAllowPDFPrint(false);
            const doc = new jsPDF();
            doc.setFontSize(12);
            const fileName = `${format(new Date(), `dd-MM-yyyy_HH-mm`)} Participant Action Plan Detail`;
            doc.html(templateContainerRef.current, {
                async callback(doc) {
                    createFooter(doc);
                    doc.save(fileName);
                },
                autoPaging: 'text',
                margin: [10, 0, 10, 0],
                width: 210,
                windowWidth: 1050
            });
        }
    }, [templateContainerRef.current]);

    //HELPER fNS
    const createFooter = (doc) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageSize = doc.internal.pageSize;

        doc.setFontSize(8);

        Array.from({ length: pageCount }, (_, i) => i + 1).forEach((pageNumber) => {
            doc.setPage(pageNumber);
            const footerRight =
                ` Printed by ${loggedInUser?.firstName} ${loggedInUser?.lastName},` +
                ` email: ${loggedInUser.loginEmailAddress},` +
                ` on ${format(new Date(), 'EEEE, d MMMM yyyy, HH:mm')},` +
                ` Document Id: ${documentId}`;
            const footerSpacing = 130 - footerRight.length;
            const footerText =
                `     Page ${pageNumber} of ${pageCount}     ` +
                ' '.repeat(footerSpacing > 0 ? footerSpacing : 5) +
                footerRight;

            doc.text(footerText, 1, pageSize.height - 5, { align: 'left' });
        });
    };

    // RENDER
    return (
        <div className={classes.pdfModal}>
            <div className={classes.pdfModalContent}>
                <div
                    id="contentTemplateContainer"
                    className={pdf.pdfWrapper}
                    ref={templateContainerRef}>
                    <div className={pdf.section}>
                        <h3
                            className={`${pdf.pdfTableHeaders} ${pdf.rowFilled} ${classes.removeHeaderMargin}`}>
                            Participant Details
                        </h3>
                        <div className={pdf.dualColumnSet}>
                            <div className={pdf.column}>
                                <div className={pdf.row}>
                                    <span>Participant ID</span>
                                    <span>{participant.ptCode || '-'}</span>
                                </div>
                                <div className={pdf.row}>
                                    <span>Participant Name</span>
                                    <span>{`${participant.firstName} ${participant.lastName}`}</span>
                                </div>
                                <div className={pdf.row}>
                                    <span>Date of Print</span>
                                    <span className={classes.printFont}>
                                        {formatDate(Date.now())}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={pdf.section}>
                        <h3
                            className={`${pdf.pdfTableHeaders} ${pdf.rowFilled} ${classes.removeHeaderMargin}`}>
                            Barriers
                        </h3>
                        <table className={classes.pdfTableBorders}>
                            <thead>
                                <tr>
                                    <td>Barrier Name</td>
                                    <td>Start Date</td>
                                    <td>End Date</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className={classes.barrierHead}>
                                    <td colSpan="3">Current Barriers</td>
                                </tr>
                                {barriers.map((barrier) => {
                                    if (new Date() <= new Date(barrier.dueDate)) {
                                        return (
                                            <tr className={pdf.rowPlain} key={barrier.id}>
                                                <td>{barrier.barrier}</td>
                                                <td>{formatDate(barrier.startDate)}</td>
                                                <td>{formatDate(barrier.completeDate)}</td>
                                            </tr>
                                        );
                                    }
                                })}
                                <tr className={classes.barrierHead}>
                                    <td colSpan="3">Expired Barriers</td>
                                </tr>
                                {barriers.map((barrier) => {
                                    if (new Date() > new Date(barrier.dueDate)) {
                                        return (
                                            <tr className={pdf.rowPlain} key={barrier.id}>
                                                <td>{barrier.barrier}</td>
                                                <td>{formatDate(barrier.startDate)}</td>
                                                <td>{formatDate(barrier.completeDate)}</td>
                                            </tr>
                                        );
                                    }
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className={pdf.section}>
                        <h3
                            className={`${pdf.pdfTableHeaders} ${pdf.rowFilled} ${classes.removeHeaderMargin}`}>
                            Action Plan
                        </h3>
                        <table className={`${classes.pdfTableBorders} ${classes.actionTable}`}>
                            <thead>
                                <tr>
                                    <td>Start Date</td>
                                    <td>Completion Date</td>
                                    <td>Mandatory</td>
                                    <td>Outcome</td>
                                    <td>Time Spent</td>
                                    <td>Barriers</td>
                                </tr>
                            </thead>
                            <tbody>
                                {actions.map((action) => {
                                    return (
                                        <Fragment key={action.id}>
                                            <tr className={classes.pdfTableBorders}>
                                                <td colSpan="6">
                                                    Action: {action.actionTitle} - {action.action}
                                                </td>
                                            </tr>
                                            <tr className={classes.pdfTableBorders}>
                                                <td colSpan="1">
                                                    {formatDate(action.actionStartDate)}
                                                </td>
                                                <td colSpan="1">
                                                    {formatDate(action.actionOutcomeDate)}
                                                </td>
                                                <td colSpan="1">
                                                    {action.mandatory ? 'Yes' : 'No'}
                                                </td>
                                                <td colSpan="3"></td>
                                            </tr>
                                            <tr className={classes.pdfTableBorders}>
                                                <td colSpan="6">Notes: {action.comments}</td>
                                            </tr>
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Button type="button" content="Close" onClick={onClosePdfPreview} />
        </div>
    );
};

GenerateActionPlanPDF.propTypes = {
    participant: PropTypes.shape({
        contractId: PropTypes.string,
        eligibilityGroupId: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        ptCode: PropTypes.string
    }),
    barriers: PropTypes.object,
    actions: PropTypes.object,
    templateContainerRef: PropTypes.object,
    onClosePdfPreview: PropTypes.func
};

export default GenerateActionPlanPDF;
